import React, { useState, useEffect, useRef } from "react";
import Checkbox from "@material-ui/core/Checkbox";
import InputLabel from "@material-ui/core/InputLabel";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import { FixedSizeList } from "react-window";

import { MenuProps, useStyles } from "./utils";

function SearchableMultiSelect({
  label,
  options,
  onChange,
  value,
  keyField,
  valueField,
  name,
}) {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState(value); // New state for selected values
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  useEffect(() => {
    const allSelected = selectedValues.length === options.length
      && selectedValues.every((
        selectedValue,
      ) => options.some((option) => option[keyField] === selectedValue));
    setSelectAllChecked(allSelected);
  }, [selectedValues, options, keyField]);

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleTextFieldClick = (e) => {
    e.stopPropagation();
  };

  const handleTextFieldKeyDown = (e) => {
    e.stopPropagation();
  };

  const handleSelectAllClick = () => {
    const allOptionKeys = options.map((option) => option[keyField]);
    if (selectAllChecked) {
      setSelectedValues([]); // Update selected values
    } else {
      setSelectedValues(allOptionKeys); // Update selected values
    }
  };

  const handleApplyClick = () => {
    onChange(selectedValues); // Submit selected values
    setOpen(false);
  };

  const handleSelectChange = (event, option) => {
    setSelectedValues((prevSelectedValues) => {
      if (prevSelectedValues.includes(option[keyField])) {
        return prevSelectedValues.filter((item) => item !== option[keyField]);
      }
      return [...prevSelectedValues, option[keyField]];
    });
  };

  const filteredOptions = options.filter(
    (option) => option[valueField].toLowerCase().includes(searchText.toLowerCase())
      || selectedValues.includes(option[keyField]),
  );

  const optionItem = ({ index, style }) => (
    <MenuItem
      style={{
        ...style,
        whiteSpace: "normal",
        padding: "0 5px",
        height: "30px",
        minHeight: "auto",
      }}
      key={filteredOptions[index][keyField]}
      value={filteredOptions[index][keyField]}
      onClick={(event) => handleSelectChange(event, filteredOptions[index])}
    >
      <ListItemIcon style={{ minWidth: 0, padding: 0 }}>
        <Checkbox
          style={{ minWidth: 0, padding: "0 4px" }}
          checked={selectedValues.includes(filteredOptions[index][keyField])}
        />
      </ListItemIcon>
      <ListItemText
        className={classes.item}
        primary={`${filteredOptions[index][valueField]}-${filteredOptions[index][keyField]}`}
      />
    </MenuItem>
  );

  return (
    <FormControl className={classes.formControl}>
      <InputLabel
        htmlFor="mutiple-select-label"
        style={{ paddingLeft: "18px", marginTop: "-7px" }}
      >
        {label}
      </InputLabel>
      <Select
        labelId="mutiple-select-label"
        id="mutiple-select-label"
        multiple
        value={selectedValues.length ? selectedValues : [`All ${name}`]}
        label={label}
        variant="outlined"
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            style: {
              maxHeight: "400px",
              overflowY: "hidden",
              paddingBottom: "30px",
              width: "200px",
            },
          },
        }}
        renderValue={(selected) => {
          const selectedOption = options.find(
            (option) => option[keyField] === selected[0],
          );
          if (selected.length > 1 && selected.length < options.length) {
            return (
              <span style={{ fontWeight: "bold" }}>Multiple Selected</span>
            );
          }
          if (selected.length === 1) {
            return (
              <span style={{ fontWeight: "bold" }}>
                {selectedOption ? selectedOption[valueField] : selected[0]}
              </span>
            );
          }
          return (
            <span style={{ fontWeight: "bold" }}>
              All
              {name}
            </span>
          );
        }}
        MenuListProps={{
          ref: dropdownRef,
        }}
        open={open}
        onOpen={() => setOpen(true)} // Handle when the dropdown opens
        onClose={() => setOpen(false)}
      >
        <MenuItem
          onClick={(e) => e.stopPropagation()}
          style={{ whiteSpace: "normal", fontSize: 14, padding: "0 5px" }}
        >
          <TextField
            label="Search"
            value={searchText}
            onChange={handleSearchTextChange}
            onClick={handleTextFieldClick}
            onKeyDown={handleTextFieldKeyDown}
            fullWidth
          />
        </MenuItem>
        <MenuItem
          key="all"
          value="All"
          onClick={handleSelectAllClick}
          style={{ whiteSpace: "normal", fontSize: 14, padding: "0 5px" }}
        >
          <ListItemIcon style={{ minWidth: 0 }}>
            <Checkbox
              checked={selectAllChecked}
              style={{ minWidth: 0, padding: "5px 4px" }}
              indeterminate={
                selectedValues.length > 0
                && selectedValues.length < options.length
              }
            />
          </ListItemIcon>
          <ListItemText className={classes.item} primary="Select All" />
        </MenuItem>
        <FixedSizeList
          height={220}
          itemCount={filteredOptions.length}
          itemSize={30}
          width="100%"
          style={{ padding: 0, margin: 0 }}
        >
          {optionItem}
        </FixedSizeList>
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "white",
            display: "flex",
            justifyContent: "center",
            margin: "-30px",
          }}
        >
          <button
            type="button"
            onClick={handleApplyClick}
            style={{
              width: "100%",
              height: "40px",
              backgroundColor: "#f47c21",
              color: "white",
              border: "none",
            }}
          >
            Apply
          </button>
        </div>
      </Select>
    </FormControl>
  );
}

export default SearchableMultiSelect;
